import type { SpellDefinition } from '../types/spell';
import { SpellAffiliation } from '../types/spell';
import type { CharacterClassId, CharacterRaceId } from '../types/character';
import { NON_MAGIC_CLASS_IDS } from '../types/character';
import { IMPAIR, INVISIBILITY } from '../constants';

// Clases mágicas = todas menos las "sin magia". La invisibilidad a maná la
// aprenden todas las mágicas; la variante marcial (a HP) sólo las sin magia.
const NON_MAGIC_CLASSES: CharacterClassId[] = [...NON_MAGIC_CLASS_IDS];

// Shorthands de afiliación usados por el catálogo (ver SpellAffiliation /
// docs/SPELLS.md §Afiliación de targeting). Para categorías futuras (OWN_SUMMON,
// OWN_TAMED, NEUTRAL_NPC) usar `SpellAffiliation.X` directo en el spell que las necesite.
const { SELF, ALLY_PLAYER, ENEMY_PLAYER, HOSTILE_NPC } = SpellAffiliation;

// ============================================================================
// SPELL DEFINITIONS - 39 spells
// ============================================================================

export const SPELLS: Record<string, SpellDefinition> = {

  // =========================================================================
  // 1. RAYO ARCANO -- Wizard bread-and-butter nuke
  // =========================================================================
  spell_rayo_arcano: {
    id: 'spell_rayo_arcano',
    affects: [HOSTILE_NPC, ENEMY_PLAYER],
    name: 'Arcane Ray',
    nameEs: 'Rayo Arcano',
    description: 'Fires a concentrated beam of arcane energy at a target.',
    descriptionEs: 'Dispara un rayo concentrado de energía arcana al objetivo.',
    icon: 'spell_rayo_arcano',
    castType: 'target',
    targetType: 'players_and_npcs',
    range: 9,
    castTime: 0.5,
    interruptible: true,
    cooldown: 2,
    costs: [{ resource: 'mana', amount: 25, mode: 'flat' }],
    effects: [
      { type: 'damage', damageType: 'magical', minDamage: 20, maxDamage: 35, scalingFactor: 0.8 },
    ],
    maxActiveInstances: 1,
    spellWords: 'Rah Sar',
    requirements: { minLevel: 1, allowedClasses: ['wizard', 'druid', 'bard'] },
    tags: ['offensive', 'pve', 'hybrid'],
  },

  // =========================================================================
  // HECHIZO OTORGADO POR ITEM (grantOnly) -- demo de `Item.grantsSpellId`.
  // No se auto-aprende: sólo lo tenés mientras el anillo que lo otorga esté
  // equipado (ver ring_spellbound). Al desequipar se olvida y sale de la hotbar.
  // =========================================================================
  spell_amulet_soul_bolt: {
    id: 'spell_amulet_soul_bolt',
    affects: [HOSTILE_NPC, ENEMY_PLAYER],
    name: 'Soul Bolt',
    nameEs: 'Dardo de Alma',
    description: 'A bolt of bound soul-energy, wielded only while the amulet is worn.',
    descriptionEs: 'Un dardo de energía anímica, sólo empuñable mientras portes el amuleto.',
    icon: 'spell_rayo_arcano',
    castType: 'target',
    targetType: 'players_and_npcs',
    range: 9,
    castTime: 0.5,
    interruptible: true,
    cooldown: 2,
    grantOnly: true,
    costs: [{ resource: 'mana', amount: 20, mode: 'flat' }],
    effects: [
      { type: 'damage', damageType: 'magical', minDamage: 22, maxDamage: 38, scalingFactor: 0.8 },
    ],
    maxActiveInstances: 1,
    spellWords: 'Ánima Vor',
    requirements: { minLevel: 1 },
    tags: ['offensive', 'pve'],
  },

  // =========================================================================
  // 2. TORMENTA DE FUEGO -- Area damage + burn DoT
  // =========================================================================
  spell_tormenta_fuego: {
    id: 'spell_tormenta_fuego',
    affects: [HOSTILE_NPC, ENEMY_PLAYER],
    name: 'Firestorm',
    nameEs: 'Tormenta de Fuego',
    description: 'Rains fire upon a 5x5 area, burning all enemies caught within.',
    descriptionEs: 'Lluvia de fuego sobre un área de 5x5, quemando a todos los enemigos.',
    icon: 'spell_tormenta_fuego',
    castType: 'area',
    targetType: 'ground',
    range: 8,
    minRange: 2, // artillería: no podés dejar caer la tormenta encima tuyo
    areaSize: 5,
    castTime: 1.5,
    castAnimationId: 1,
    interruptible: true,
    cooldown: 12,
    costs: [{ resource: 'mana', amount: 25, mode: 'flat' }],
    effects: [
      { type: 'damage', damageType: 'magical', minDamage: 40, maxDamage: 65, scalingFactor: 1.2, rangeFalloff: { fullFromTiles: 5, minMultiplier: 0.6 } },
      { type: 'status_effect', statusType: 'burn', duration_ms: 5000, tickInterval_ms: 1000, damagePerTick: 8, damageType: 'magical', stackBehavior: 'refresh' },
    ],
    maxActiveInstances: 1,
    spellWords: 'Flam Rah Ket',
    requirements: { minLevel: 15, allowedClasses: ['wizard'] },
    tags: ['offensive', 'pve', 'hybrid'],
  },

  // =========================================================================
  // 3. CURAR HERIDAS -- Basic cleric heal
  // =========================================================================
  spell_curar_heridas: {
    id: 'spell_curar_heridas',
    affects: [SELF, ALLY_PLAYER],
    name: 'Cure Wounds',
    nameEs: 'Curar Heridas',
    description: 'Heals all allies in a 3x3 area.',
    descriptionEs: 'Sana a todos los aliados en un área de 3x3.',
    icon: 'spell_curar_heridas',
    castType: 'area',
    targetType: 'ground',
    range: 6,
    areaSize: 3,
    castTime: 0,
    interruptible: false,
    cooldown: 3,
    costs: [{ resource: 'mana', amount: 25, mode: 'flat' }],
    effects: [
      { type: 'heal', minHeal: 35, maxHeal: 55, scalingFactor: 0.6 },
    ],
    maxActiveInstances: 1,
    spellWords: 'Lux Sanar',
    requirements: { minLevel: 1, allowedClasses: ['cleric', 'paladin', 'druid'] },
    tags: ['support', 'heal', 'pve', 'hybrid'],
  },

  // =========================================================================
  // 4. ESCUDO DIVINO -- Defensive buff
  // =========================================================================
  spell_escudo_divino: {
    id: 'spell_escudo_divino',
    affects: [SELF, ALLY_PLAYER],
    name: 'Divine Shield',
    nameEs: 'Escudo Divino',
    description: 'Wraps a target in a golden barrier, boosting defenses.',
    descriptionEs: 'Envuelve al objetivo en una barrera dorada que aumenta sus defensas.',
    icon: 'spell_escudo_divino',
    castType: 'target',
    targetType: 'players',
    range: 6,
    castTime: 0.5,
    interruptible: true,
    cooldown: 15,
    costs: [{ resource: 'mana', amount: 25, mode: 'flat' }],
    effects: [
      { type: 'buff', modifiers: { physDef: 20, magicDef: 15 }, duration_ms: 20000, durationType: 'fixed', stackBehavior: 'refresh', buffIdPrefix: 'escudo_divino', activeFxId: 'fx_shield_dome_aura' },
    ],
    maxActiveInstances: 1,
    spellWords: 'Aegis Lux',
    requirements: { minLevel: 5, allowedClasses: ['cleric', 'paladin'] },
    tags: ['support', 'defensive', 'hybrid'],
  },

  // =========================================================================
  // 5. AURA DE FUEGO -- Toggle aura, damages nearby enemies
  // =========================================================================
  spell_aura_fuego: {
    id: 'spell_aura_fuego',
    affects: [HOSTILE_NPC, ENEMY_PLAYER],
    name: 'Fire Aura',
    nameEs: 'Aura de Fuego',
    description: 'Toggle: Surrounds you in flames that burn nearby enemies. Reserves 20% mana.',
    descriptionEs: 'Toggle: Te rodeas de llamas que queman a los enemigos cercanos. Reserva 20% de maná.',
    icon: 'spell_aura_fuego',
    castType: 'toggle',
    targetType: 'self',
    range: 0,
    castTime: 1.5,
    castAnimationId: 1,
    interruptible: true,
    cooldown: 2,
    costs: [],
    effects: [
      { type: 'damage', damageType: 'magical', minDamage: 5, maxDamage: 10, scalingFactor: 0.3 },
    ],
    onHitFxId: 'fx_fire_aura_hit',
    aura: { radiusTiles: 1, affectsTarget: 'enemies', tickInterval_ms: 2000, particleEffectId: 'fx_fire_ring_aura', manaReservePercent: 0.20 },
    maxActiveInstances: 1,
    spellWords: 'Ignis Aura',
    requirements: { minLevel: 10, allowedClasses: ['wizard', 'druid'] },
    tags: ['aura', 'toggle', 'offensive', 'pve'],
  },

  // =========================================================================
  // 6. AURA DE HIELO -- Toggle aura, slows enemies
  // =========================================================================
  spell_aura_hielo: {
    id: 'spell_aura_hielo',
    affects: [HOSTILE_NPC, ENEMY_PLAYER],
    name: 'Frost Aura',
    nameEs: 'Aura de Hielo',
    description: 'Toggle: Slows all enemies around you. Reserves 15% mana.',
    descriptionEs: 'Toggle: Ralentiza a todos los enemigos a tu alrededor. Reserva 15% de maná.',
    icon: 'spell_aura_hielo',
    castType: 'toggle',
    targetType: 'self',
    range: 0,
    castTime: 2.0,
    castAnimationId: 1,
    interruptible: true,
    cooldown: 2,
    costs: [],
    effects: [
      { type: 'debuff', modifiers: { moveSpeed: -0.3, attackSpeed: -0.15 }, duration_ms: 3000, durationType: 'fixed', stackBehavior: 'refresh', buffIdPrefix: 'aura_hielo_slow' },
    ],
    aura: { radiusTiles: 4, affectsTarget: 'enemies', tickInterval_ms: 1500, particleEffectId: 'fx_frost_orbit_aura', manaReservePercent: 0.15 },
    maxActiveInstances: 1,
    spellWords: 'Gelidum Aura',
    requirements: { minLevel: 12, allowedClasses: ['wizard', 'cleric'] },
    tags: ['aura', 'toggle', 'debuff', 'crowd_control', 'pvp'],
  },

  // =========================================================================
  // 7. GRITO DE GUERRA -- Toggle aura, buffs allies (stamina drain)
  // =========================================================================
  spell_grito_guerra: {
    id: 'spell_grito_guerra',
    affects: [SELF, ALLY_PLAYER],
    name: 'War Cry',
    nameEs: 'Grito de Guerra',
    description: 'Toggle: Inspires nearby allies, increasing damage and attack speed. Drains stamina.',
    descriptionEs: 'Toggle: Inspira a los aliados cercanos, aumentando daño y velocidad de ataque. Drena stamina.',
    icon: 'spell_grito_guerra',
    castType: 'toggle',
    targetType: 'self',
    range: 0,
    castTime: 1.0,
    interruptible: false,
    cooldown: 3,
    costs: [{ resource: 'stamina', amount: 5, mode: 'per_second' }],
    effects: [
      { type: 'buff', modifiers: { damagePercent: 0.15, attackSpeedPercent: 0.10 }, duration_ms: 3000, durationType: 'fixed', stackBehavior: 'refresh', buffIdPrefix: 'grito_guerra' },
    ],
    aura: { radiusTiles: 5, affectsTarget: 'allies', tickInterval_ms: 2000, particleEffectId: 'particle_war_cry' },
    maxActiveInstances: 1,
    spellWords: 'RAH HAR',
    requirements: { minLevel: 8, allowedClasses: ['warrior', 'paladin', 'pirate', 'bard'] },
    tags: ['aura', 'toggle', 'support', 'hybrid'],
  },

  // =========================================================================
  // 8. INVOCAR LOBO -- Summon wolf companion
  // =========================================================================
  spell_invocar_lobo: {
    id: 'spell_invocar_lobo',
    affects: [SELF],
    name: 'Summon Wolf',
    nameEs: 'Invocar Lobo',
    description:
      'Toggle: summons a spirit wolf that fights alongside you. Reserves 20% mana. If slain, it returns after a delay.',
    descriptionEs:
      'Toggle: invoca un lobo espiritual que lucha a tu lado. Reserva 20% de maná. Si lo matan, vuelve tras un tiempo.',
    icon: 'spell_invocar_lobo',
    castType: 'toggle',
    targetType: 'self',
    range: 0,
    castTime: 2.0,
    castAnimationId: 1,
    interruptible: true,
    cooldown: 3,
    costs: [],
    effects: [
      {
        type: 'summon',
        profileId: 'summon_wolf',
        npcTemplateId: 'summon_wolf',
        count: 1,
        duration_ms: 0,
        maxActiveSummons: 1,
        behavior: 'follow_and_attack',
      },
    ],
    // El SummonSystem consume la reserva; el campo aura hace que el toggle path la reconozca.
    aura: { radiusTiles: 0, tickInterval_ms: 1000, manaReservePercent: 0.2 },
    maxActiveInstances: 1,
    spellWords: 'Ven, hermano lobo',
    requirements: { minLevel: 5, allowedClasses: ['druid', 'hunter', 'wizard'] },
    tags: ['summon', 'toggle', 'pve', 'hybrid'],
  },

  // =========================================================================
  // 9. INVOCAR ESQUELETO -- Summon skeletons (HP cost = high risk)
  // =========================================================================
  spell_invocar_esqueleto: {
    id: 'spell_invocar_esqueleto',
    affects: [SELF],
    name: 'Summon Skeleton',
    nameEs: 'Invocar Esqueleto',
    description: 'Raises skeletal warriors from the ground. Costs HP to cast.',
    descriptionEs: 'Levanta guerreros esqueléticos del suelo. Cuesta HP para castear.',
    icon: 'spell_invocar_esqueleto',
    castType: 'self',
    targetType: 'self',
    range: 0,
    castTime: 1.5,
    interruptible: true,
    cooldown: 20,
    costs: [
      { resource: 'mana', amount: 25, mode: 'flat' },
      { resource: 'hp', amount: 30, mode: 'flat' },
    ],
    effects: [
      { type: 'summon', npcTemplateId: 'summon_skeleton', count: 2, duration_ms: 90000, maxActiveSummons: 4, behavior: 'follow_and_attack' },
    ],
    maxActiveInstances: 4,
    spellWords: 'Mort Ek Rah',
    requirements: { minLevel: 10, allowedClasses: ['wizard', 'cleric'] },
    tags: ['summon', 'high_risk', 'pve'],
  },

  // =========================================================================
  // 10. INVOCAR GOLEM -- Summon tanky golem
  // =========================================================================
  spell_invocar_golem: {
    id: 'spell_invocar_golem',
    affects: [SELF],
    name: 'Summon Golem',
    nameEs: 'Invocar Golem',
    description: 'Shapes earth and stone into a powerful golem guardian.',
    descriptionEs: 'Da forma a tierra y piedra en un poderoso golem guardián.',
    icon: 'spell_invocar_golem',
    castType: 'self',
    targetType: 'self',
    range: 0,
    castTime: 3.0,
    interruptible: true,
    cooldown: 60,
    costs: [{ resource: 'mana', amount: 25, mode: 'flat' }],
    effects: [
      { type: 'summon', npcTemplateId: 'summon_golem', count: 1, duration_ms: 180000, maxActiveSummons: 1, behavior: 'follow_and_attack' },
    ],
    maxActiveInstances: 1,
    spellWords: 'Terra Mak Gorun',
    requirements: { minLevel: 20, allowedClasses: ['wizard', 'druid', 'paladin'] },
    tags: ['summon', 'defensive', 'pve'],
  },

  // Invocar Centinela Vidente (Clérigo, toggle): centinela ESTÁTICO con 100 de vida que
  // castea el Pulso Revelador seguido, detectando invisibles cercanos. Ver docs/INVISIBILITY.md.
  spell_invocar_centinela: {
    id: 'spell_invocar_centinela',
    affects: [SELF],
    name: 'Summon Seer Sentinel',
    nameEs: 'Invocar Centinela Vidente',
    description: 'Toggle: raises a stationary sentinel that reveals nearby invisible enemies. Reserves 10% mana.',
    descriptionEs: 'Toggle: alza un centinela inmóvil que revela a los enemigos invisibles cercanos. Reserva 10% de maná.',
    icon: 'spell_invocar_golem',
    castType: 'toggle',
    targetType: 'self',
    range: 0,
    castTime: 2.0,
    interruptible: true,
    cooldown: 3,
    costs: [],
    effects: [
      { type: 'summon', profileId: 'summon_sentinel', npcTemplateId: 'summon_sentinel', count: 1, duration_ms: 0, maxActiveSummons: 1, behavior: 'follow_and_attack' },
    ],
    aura: { radiusTiles: 0, tickInterval_ms: 1000, manaReservePercent: 0.1 },
    maxActiveInstances: 1,
    spellWords: 'Vigil Lux',
    requirements: { minLevel: 14, allowedClasses: ['cleric'] },
    tags: ['summon', 'toggle', 'support', 'pvp'],
  },

  // Invocar Espíritu Vidente del Bosque (Clérigo, toggle): espíritu que te sigue y castea el
  // Pulso Revelador cada 2 minutos (detección móvil más esporádica). Ver docs/INVISIBILITY.md.
  spell_invocar_espiritu_vidente: {
    id: 'spell_invocar_espiritu_vidente',
    affects: [SELF],
    name: 'Summon Forest Seer',
    nameEs: 'Invocar Espíritu Vidente',
    description: 'Toggle: summons a wandering forest spirit that reveals invisible enemies every 2 minutes. Reserves 10% mana.',
    descriptionEs: 'Toggle: invoca un espíritu del bosque que revela a los invisibles cada 2 minutos. Reserva 10% de maná.',
    icon: 'spell_invocar_golem',
    castType: 'toggle',
    targetType: 'self',
    range: 0,
    castTime: 2.5,
    interruptible: true,
    cooldown: 3,
    costs: [],
    effects: [
      { type: 'summon', profileId: 'summon_seer_spirit', npcTemplateId: 'summon_seer_spirit', count: 1, duration_ms: 0, maxActiveSummons: 1, behavior: 'follow_and_attack' },
    ],
    aura: { radiusTiles: 0, tickInterval_ms: 1000, manaReservePercent: 0.1 },
    maxActiveInstances: 1,
    spellWords: 'Silva Vigil',
    requirements: { minLevel: 16, allowedClasses: ['cleric'] },
    tags: ['summon', 'toggle', 'support', 'pve'],
  },

  // =========================================================================
  // 11. PACTO DE SANGRE -- High-risk: 25% HP cost nuke
  // =========================================================================
  spell_pacto_sangre: {
    id: 'spell_pacto_sangre',
    affects: [HOSTILE_NPC, ENEMY_PLAYER],
    name: 'Blood Pact',
    nameEs: 'Pacto de Sangre',
    description: 'Sacrifices 25% of your current HP to unleash devastating dark damage.',
    descriptionEs: 'Sacrifica el 25% de tu HP actual para desatar daño oscuro devastador.',
    icon: 'spell_pacto_sangre',
    castType: 'target',
    targetType: 'players_and_npcs',
    range: 6,
    castTime: 0.3,
    interruptible: false,
    cooldown: 8,
    costs: [{ resource: 'mana', amount: 25, mode: 'flat' }],
    effects: [
      { type: 'damage', damageType: 'magical', minDamage: 60, maxDamage: 100, scalingFactor: 1.5 },
    ],
    maxActiveInstances: 1,
    spellWords: 'Sangre... por poder',
    requirements: { minLevel: 15, allowedClasses: ['wizard', 'cleric', 'rogue'] },
    tags: ['offensive', 'high_risk', 'pvp', 'hybrid'],
  },

  // =========================================================================
  // 12. SACRIFICIO -- Extreme risk: burn ALL mana for screen-wide AoE
  // =========================================================================
  spell_sacrificio: {
    id: 'spell_sacrificio',
    affects: [HOSTILE_NPC, ENEMY_PLAYER],
    name: 'Sacrifice',
    nameEs: 'Sacrificio',
    description: 'Burns all remaining mana to unleash a devastating screen-wide explosion.',
    descriptionEs: 'Quema todo el maná restante para desatar una explosión devastadora en pantalla.',
    icon: 'spell_sacrificio',
    castType: 'screen',
    targetType: 'players_and_npcs',
    range: 0,
    castTime: 2.0,
    interruptible: true,
    cooldown: 120,
    costs: [{ resource: 'mana', amount: 25, mode: 'flat' }],
    effects: [
      { type: 'damage', damageType: 'magical', minDamage: 100, maxDamage: 200, scalingFactor: 2.0 },
      { type: 'status_effect', statusType: 'burn', duration_ms: 8000, tickInterval_ms: 1000, damagePerTick: 15, damageType: 'magical', stackBehavior: 'override' },
    ],
    maxActiveInstances: 1,
    spellWords: 'Ignis Totalis Consumare',
    requirements: { minLevel: 25, allowedClasses: ['wizard'] },
    tags: ['offensive', 'high_risk', 'pvp'],
  },

  // =========================================================================
  // 13. FURIA BERSERKER -- HP drain buff, huge damage, reduced defense
  // =========================================================================
  spell_furia_berserker: {
    id: 'spell_furia_berserker',
    affects: [SELF],
    name: 'Berserker Fury',
    nameEs: 'Furia Berserker',
    description: 'Drains 5 HP/sec but massively increases damage and attack speed. Reduces defense.',
    descriptionEs: 'Drena 5 HP/seg pero aumenta masivamente el daño y velocidad de ataque. Reduce defensa.',
    icon: 'spell_furia_berserker',
    castType: 'self',
    targetType: 'self',
    range: 0,
    castTime: 0,
    interruptible: false,
    cooldown: 30,
    costs: [{ resource: 'hp', amount: 5, mode: 'per_second' }],
    effects: [
      { type: 'buff', modifiers: { damagePercent: 0.40, attackSpeedPercent: 0.25, critChance: 0.10, physDef: -15, magicDef: -10 }, duration_ms: 15000, durationType: 'fixed', stackBehavior: 'override', buffIdPrefix: 'furia_berserker' },
    ],
    maxActiveInstances: 1,
    spellWords: 'Sanguis Furor',
    chatWords: 'RAAAAGH!',
    requirements: { minLevel: 12, allowedClasses: ['warrior', 'bandit', 'pirate'] },
    tags: ['offensive', 'high_risk', 'pvp', 'hybrid'],
  },

  // =========================================================================
  // 14. VENENO MORTAL -- Poison DoT + slow
  // =========================================================================
  spell_veneno_mortal: {
    id: 'spell_veneno_mortal',
    affects: [HOSTILE_NPC, ENEMY_PLAYER],
    name: 'Deadly Poison',
    nameEs: 'Veneno Mortal',
    description: 'Coats your target in a potent venom that deals damage over time and slows.',
    descriptionEs: 'Cubre al objetivo en un veneno potente que causa daño continuo y ralentiza.',
    icon: 'spell_veneno_mortal',
    castType: 'target',
    targetType: 'players_and_npcs',
    range: 5,
    castTime: 0.3,
    interruptible: false,
    cooldown: 10,
    costs: [{ resource: 'stamina', amount: 30, mode: 'flat' }],
    effects: [
      { type: 'status_effect', statusType: 'poison', duration_ms: 12000, tickInterval_ms: 2000, damagePerTick: 12, damageType: 'magical', modifiers: { moveSpeed: -0.2 }, stackBehavior: 'stack' },
    ],
    maxActiveInstances: 1,
    spellWords: 'Venenum Mortis',
    requirements: { minLevel: 8, allowedClasses: ['rogue', 'bandit', 'hunter'] },
    tags: ['debuff', 'dot', 'pvp'],
  },

  // =========================================================================
  // 15. SILENCIO -- Prevents enemy spellcasting
  // =========================================================================
  spell_silencio: {
    id: 'spell_silencio',
    affects: [HOSTILE_NPC, ENEMY_PLAYER],
    name: 'Silence',
    nameEs: 'Silencio',
    description: 'Seals the target\'s ability to cast spells for 6 seconds.',
    descriptionEs: 'Sella la capacidad del objetivo de lanzar hechizos por 6 segundos.',
    icon: 'spell_silencio',
    castType: 'target',
    targetType: 'players_and_npcs',
    range: 7,
    castTime: 0.5,
    interruptible: true,
    cooldown: 25,
    costs: [{ resource: 'mana', amount: 25, mode: 'flat' }],
    effects: [
      { type: 'status_effect', statusType: 'silence', duration_ms: 6000, stackBehavior: 'refresh' },
    ],
    maxActiveInstances: 1,
    spellWords: 'Mut Sar',
    requirements: { minLevel: 12, allowedClasses: ['wizard', 'cleric', 'bard'] },
    tags: ['debuff', 'crowd_control', 'pvp'],
  },

  // =========================================================================
  // 16. CONGELAR -- Area freeze CC
  // =========================================================================
  spell_congelar: {
    id: 'spell_congelar',
    affects: [HOSTILE_NPC, ENEMY_PLAYER],
    name: 'Freeze',
    nameEs: 'Congelar',
    description: 'Freezes all enemies in a 3x3 area, immobilizing them.',
    descriptionEs: 'Paraliza a todos los enemigos en un área de 3x3 por una duración larga (entrenamiento).',
    icon: 'spell_congelar',
    castType: 'area',
    targetType: 'ground',
    range: 7,
    minRange: 2, // zona muerta: forzás posicionamiento antes de congelar
    areaSize: 3,
    castTime: 1.0,
    interruptible: true,
    cooldown: 18,
    costs: [{ resource: 'mana', amount: 25, mode: 'flat' }],
    effects: [
      // `freeze` se rutea a Paralizar de duración LARGA (variante de clase para
      // entrenar con NPCs). Ver docs/CC_IMMOBILIZE_PARALYZE.md.
      { type: 'status_effect', statusType: 'freeze', duration_ms: IMPAIR.FREEZE_PARALYZE_MS, stackBehavior: 'override' },
      { type: 'damage', damageType: 'magical', minDamage: 10, maxDamage: 20, scalingFactor: 0.4 },
    ],
    maxActiveInstances: 1,
    spellWords: 'Gelu Mortem',
    requirements: { minLevel: 10, allowedClasses: ['wizard', 'druid'] },
    tags: ['debuff', 'crowd_control', 'offensive', 'pvp'],
  },

  // =========================================================================
  // 17. MALDICION DE DEBILIDAD -- Weakening curse
  // =========================================================================
  spell_maldicion_debilidad: {
    id: 'spell_maldicion_debilidad',
    affects: [HOSTILE_NPC, ENEMY_PLAYER],
    name: 'Curse of Weakness',
    nameEs: 'Maldición de Debilidad',
    description: 'Curses a target, reducing their damage and defenses.',
    descriptionEs: 'Maldice al objetivo, reduciendo su daño y defensas.',
    icon: 'spell_maldicion_debilidad',
    castType: 'target',
    targetType: 'players_and_npcs',
    range: 7,
    castTime: 0.5,
    interruptible: true,
    cooldown: 15,
    costs: [{ resource: 'mana', amount: 25, mode: 'flat' }],
    effects: [
      { type: 'debuff', modifiers: { damagePercent: -0.20, physDef: -10, magicDef: -10 }, duration_ms: 15000, durationType: 'fixed', stackBehavior: 'refresh', buffIdPrefix: 'maldicion_debilidad' },
    ],
    maxActiveInstances: 1,
    spellWords: 'Kor Malas',
    requirements: { minLevel: 8, allowedClasses: ['wizard', 'cleric', 'druid'] },
    tags: ['debuff', 'pvp', 'hybrid'],
  },

  // =========================================================================
  // 18. REGENERACION -- Heal-over-time support
  // =========================================================================
  spell_regeneracion: {
    id: 'spell_regeneracion',
    affects: [SELF, ALLY_PLAYER],
    name: 'Regeneration',
    nameEs: 'Regeneración',
    description: 'Blesses a target with accelerated healing for 20 seconds.',
    descriptionEs: 'Bendice al objetivo con curación acelerada por 20 segundos.',
    icon: 'spell_regeneracion',
    castType: 'target',
    targetType: 'players',
    range: 6,
    castTime: 0.5,
    interruptible: true,
    cooldown: 8,
    costs: [{ resource: 'mana', amount: 25, mode: 'flat' }],
    effects: [
      { type: 'buff', modifiers: { regenHP: 3.0, regenMana: 1.0 }, duration_ms: 20000, durationType: 'fixed', stackBehavior: 'refresh', buffIdPrefix: 'regeneracion' },
    ],
    maxActiveInstances: 1,
    spellWords: 'Vita Fluo',
    requirements: { minLevel: 3, allowedClasses: ['cleric', 'druid', 'paladin', 'bard'] },
    tags: ['support', 'heal', 'pve', 'hybrid'],
  },

  // =========================================================================
  // 19. BENDICION DE MANA -- Mana restore + regen buff
  // =========================================================================
  spell_bendicion_mana: {
    id: 'spell_bendicion_mana',
    affects: [SELF, ALLY_PLAYER],
    name: 'Mana Blessing',
    nameEs: 'Bendición de Maná',
    description: 'Restores 30% of target max mana instantly.',
    descriptionEs: 'Restaura 30% del maná máximo del objetivo instantáneamente.',
    icon: 'spell_bendicion_mana',
    castType: 'target',
    targetType: 'players',
    range: 6,
    castTime: 0,
    interruptible: false,
    cooldown: 12,
    costs: [{ resource: 'mana', amount: 25, mode: 'flat' }],
    effects: [
      { type: 'mana_restore', amount: 0.30, isPercent: true },
    ],
    maxActiveInstances: 1,
    spellWords: 'Manas Fluent',
    requirements: { minLevel: 5, allowedClasses: ['cleric', 'bard', 'wizard'] },
    tags: ['support', 'hybrid'],
  },

  // =========================================================================
  // 20. INVISIBILIDAD -- Stealth (breaks on action)
  // =========================================================================
  // Invisibilidad MÁGICA (a maná): todas las clases con magia. Vuelve invisible de
  // verdad — los NPCs pierden el aggro y los demás no ven tu nombre/gráfico. Se rompe
  // apenas iniciás un ataque (melee/lanzable/hechizo ofensivo). Ver docs/INVISIBILITY.md.
  spell_invisibilidad: {
    id: 'spell_invisibilidad',
    affects: [SELF],
    name: 'Invisibility',
    nameEs: 'Invisibilidad',
    description: 'Turn invisible for 18s. NPCs lose aggro and other players cannot see your name. Breaks the moment you attack.',
    descriptionEs: 'Te vuelve invisible por 18s: los NPCs pierden el aggro y los demás no ven tu nombre. Se rompe apenas atacás.',
    icon: 'spell_invisibilidad',
    castType: 'self',
    targetType: 'self',
    range: 0,
    castTime: 1.0,
    interruptible: true,
    cooldown: 30,
    costs: [{ resource: 'mana', amount: 30, mode: 'flat' }],
    effects: [{ type: 'invisibility', duration_ms: 18000 }],
    maxActiveInstances: 1,
    spellWords: 'Umbra Velo',
    requirements: { minLevel: 10, forbiddenClasses: NON_MAGIC_CLASSES },
    tags: ['defensive', 'movement', 'pvp'],
  },

  // Invisibilidad MARCIAL (a HP): clases sin magia. Menos duración y MUCHO más
  // cooldown que la mágica, y cuesta sangre en vez de maná. Ver docs/INVISIBILITY.md.
  spell_ocultarse: {
    id: 'spell_ocultarse',
    affects: [SELF],
    name: 'Vanish',
    nameEs: 'Ocultarse',
    description: 'Vanish for 8s at the cost of your own blood. NPCs lose aggro. Breaks the moment you attack. Long cooldown.',
    descriptionEs: 'Desaparecés por 8s a costa de tu propia sangre. Los NPCs pierden el aggro. Se rompe apenas atacás. Recarga larga.',
    icon: 'spell_invisibilidad',
    castType: 'self',
    targetType: 'self',
    range: 0,
    castTime: 1.0,
    interruptible: true,
    cooldown: 75,
    costs: [{ resource: 'hp', amount: 40, mode: 'flat' }],
    effects: [{ type: 'invisibility', duration_ms: 8000 }],
    maxActiveInstances: 1,
    spellWords: 'Umbra',
    requirements: { minLevel: 12, allowedClasses: NON_MAGIC_CLASSES },
    tags: ['defensive', 'movement', 'pvp'],
  },

  // Sigilo (Asesino): invisibilidad PERMANENTE onda sigilo, pero caminás a la mitad de
  // velocidad mientras dure. Se rompe al atacar (y libera la penalización). Re-casteable
  // para volver a entrar en sigilo. Ver docs/INVISIBILITY.md.
  spell_sigilo: {
    id: 'spell_sigilo',
    affects: [SELF],
    name: 'Stealth',
    nameEs: 'Sigilo',
    description: 'Melt into the shadows: become permanently invisible while moving at half speed. Breaks the moment you attack.',
    descriptionEs: 'Te fundís en las sombras: quedás invisible de forma permanente pero caminás a la mitad de velocidad. Se rompe apenas atacás.',
    icon: 'spell_invisibilidad',
    castType: 'self',
    targetType: 'self',
    range: 0,
    castTime: 1.0,
    interruptible: true,
    cooldown: 6,
    costs: [{ resource: 'stamina', amount: 15, mode: 'flat' }],
    effects: [
      { type: 'invisibility', duration_ms: 0, permanent: true },
      // -50% velocidad mientras dure el sigilo (buffIdPrefix 'stealth_slow' → lo quita
      // clearInvisibility al romper). Duración larga: en la práctica dura hasta que atacás.
      { type: 'buff', modifiers: { moveSpeedPercent: -0.5 }, duration_ms: 3600000, durationType: 'fixed', stackBehavior: 'override', buffIdPrefix: 'stealth_slow' },
    ],
    maxActiveInstances: 1,
    spellWords: 'Umbra Silens',
    requirements: { minLevel: 15, allowedClasses: ['rogue'] },
    tags: ['defensive', 'movement', 'pvp'],
  },

  // Detectar Invisibilidad (Clérigo): revela para TODOS a los enemigos ocultos en la
  // pantalla del caster por unos segundos. Ver docs/INVISIBILITY.md.
  spell_detectar_invisibilidad: {
    id: 'spell_detectar_invisibilidad',
    affects: [ENEMY_PLAYER, HOSTILE_NPC],
    name: 'Detect Invisibility',
    nameEs: 'Detectar Invisibilidad',
    description: 'Reveals hidden enemies across your screen for a few seconds.',
    descriptionEs: 'Revela a los enemigos ocultos en toda tu pantalla por unos segundos.',
    icon: 'spell_invisibilidad',
    castType: 'screen',
    targetType: 'players_and_npcs',
    range: 0,
    castTime: 0.8,
    interruptible: true,
    cooldown: 20,
    costs: [{ resource: 'mana', amount: 30, mode: 'flat' }],
    effects: [{ type: 'reveal_invisibility', duration_ms: INVISIBILITY.REVEAL_MS }],
    maxActiveInstances: 1,
    spellWords: 'Lux Revelo',
    requirements: { minLevel: 12, allowedClasses: ['cleric'] },
    tags: ['pvp', 'support', 'defensive'],
  },

  // Pulso Revelador: variante NPC-only (grantOnly) del detector, en ÁREA autocentrada.
  // La castean las invocaciones detectoras del Clérigo (centinela / espíritu del bosque)
  // vía npcSpells periodic. Ver docs/INVISIBILITY.md / docs/SUMMONS.md.
  spell_detectar_invisibilidad_pulso: {
    id: 'spell_detectar_invisibilidad_pulso',
    affects: [ENEMY_PLAYER, HOSTILE_NPC],
    name: 'Reveal Pulse',
    nameEs: 'Pulso Revelador',
    description: 'A pulse that reveals hidden enemies around the caster.',
    descriptionEs: 'Un pulso que revela a los enemigos ocultos alrededor del lanzador.',
    icon: 'spell_invisibilidad',
    castType: 'area',
    targetType: 'players_and_npcs',
    range: 0,
    areaSize: 13,
    castTime: 0,
    interruptible: false,
    cooldown: 8,
    costs: [],
    effects: [{ type: 'reveal_invisibility', duration_ms: INVISIBILITY.REVEAL_MS }],
    maxActiveInstances: 1,
    grantOnly: true,
    requirements: { minLevel: 1 },
    tags: ['pve', 'support'],
  },

  // =========================================================================
  // 21. DESCARGA ELECTRICA -- Single-target lightning nuke
  // =========================================================================
  spell_descarga_electrica: {
    id: 'spell_descarga_electrica',
    affects: [HOSTILE_NPC, ENEMY_PLAYER],
    name: 'Lightning Bolt',
    nameEs: 'Descarga Eléctrica',
    description: 'Strikes the target with lightning, dealing heavy instant damage.',
    descriptionEs: 'Golpea al objetivo con un rayo, causando gran daño instantáneo.',
    icon: 'spell_descarga_electrica',
    castType: 'target',
    targetType: 'players_and_npcs',
    range: 10,
    castTime: 0.8,
    interruptible: true,
    cooldown: 6,
    costs: [{ resource: 'mana', amount: 25, mode: 'flat' }],
    effects: [
      { type: 'damage', damageType: 'magical', minDamage: 35, maxDamage: 60, scalingFactor: 1.0 },
    ],
    maxActiveInstances: 1,
    spellWords: 'Rah Ket Ful',
    requirements: { minLevel: 8, allowedClasses: ['wizard', 'druid'] },
    tags: ['offensive', 'pve', 'hybrid'],
  },

  // =========================================================================
  // 22. CANCION DEL VALOR -- Bard screen-wide group buff
  // =========================================================================
  spell_cancion_valor: {
    id: 'spell_cancion_valor',
    // FIX: antes buffeaba a TODOS los players en pantalla (incluidos rivales). Ahora
    // sólo caster + aliados de facción, gracias al filtro de afiliación.
    affects: [SELF, ALLY_PLAYER],
    name: 'Song of Valor',
    nameEs: 'Canción del Valor',
    description: 'Sings an inspiring melody that boosts all allies on screen.',
    descriptionEs: 'Canta una melodía inspiradora que potencia a todos los aliados en pantalla.',
    icon: 'spell_cancion_valor',
    castType: 'screen',
    targetType: 'players',
    range: 0,
    castTime: 1.0,
    interruptible: true,
    cooldown: 30,
    costs: [{ resource: 'mana', amount: 25, mode: 'flat' }],
    effects: [
      { type: 'buff', modifiers: { damagePercent: 0.10, critChance: 0.05, moveSpeedPercent: 0.10 }, duration_ms: 30000, durationType: 'fixed', stackBehavior: 'refresh', buffIdPrefix: 'cancion_valor' },
    ],
    maxActiveInstances: 1,
    spellWords: '(canta una melodía heroica)',
    requirements: { minLevel: 10, allowedClasses: ['bard'] },
    tags: ['support', 'hybrid'],
  },

  // =========================================================================
  // 23. DRENAR VIDA -- Damage enemy + heal self
  // =========================================================================
  spell_drenar_vida: {
    id: 'spell_drenar_vida',
    affects: [HOSTILE_NPC, ENEMY_PLAYER],
    name: 'Life Drain',
    nameEs: 'Drenar Vida',
    description: 'Drains life force from the target, healing yourself.',
    descriptionEs: 'Drena la fuerza vital del objetivo, sanándote a ti mismo.',
    icon: 'spell_drenar_vida',
    castType: 'target',
    targetType: 'players_and_npcs',
    range: 6,
    castTime: 1.0,
    interruptible: true,
    cooldown: 10,
    costs: [{ resource: 'mana', amount: 25, mode: 'flat' }],
    effects: [
      { type: 'damage', damageType: 'magical', minDamage: 25, maxDamage: 40, scalingFactor: 0.7 },
      { type: 'heal', minHeal: 20, maxHeal: 35, scalingFactor: 0.5 },
    ],
    maxActiveInstances: 1,
    spellWords: 'Vith Drak',
    requirements: { minLevel: 8, allowedClasses: ['wizard', 'cleric', 'druid'] },
    tags: ['offensive', 'heal', 'hybrid'],
  },

  // =========================================================================
  // 24. TELETRANSPORTE -- Blink to target tile
  // =========================================================================
  spell_teletransporte: {
    id: 'spell_teletransporte',
    // Movimiento puro: el effect teleport se aplica al caster fuera del loop de targets.
    affects: [SELF],
    name: 'Teleport',
    nameEs: 'Teletransporte',
    description: 'Instantly teleport to a target tile within range.',
    descriptionEs: 'Teletransportarse instantáneamente a un tile objetivo dentro del rango.',
    icon: 'spell_teletransporte',
    castType: 'area',
    targetType: 'ground',
    range: 8,
    areaSize: 1,
    castTime: 0.3,
    interruptible: false,
    cooldown: 15,
    costs: [
      { resource: 'mana', amount: 25, mode: 'flat' },
      { resource: 'stamina', amount: 20, mode: 'flat' },
    ],
    effects: [
      { type: 'teleport' },
    ],
    maxActiveInstances: 1,
    spellWords: 'Rah Ket',
    requirements: { minLevel: 12, allowedClasses: ['wizard', 'rogue', 'bard'] },
    tags: ['movement', 'pvp', 'hybrid'],
  },

  // =========================================================================
  // 26. FURIA ANCESTRAL -- Orc-only melee buff (race-locked)
  // =========================================================================
  spell_furia_ancestral: {
    id: 'spell_furia_ancestral',
    affects: [SELF],
    name: 'Ancestral Fury',
    nameEs: 'Furia Ancestral',
    description: 'Invokes the rage of your orcish ancestors, boosting damage and defense for 12 seconds.',
    descriptionEs: 'Invoca la furia de tus ancestros orcos, aumentando daño y defensa por 12 segundos.',
    icon: 'passive_orc_keystone_1',
    castType: 'self',
    targetType: 'self',
    range: 0,
    castTime: 0,
    interruptible: false,
    cooldown: 25,
    costs: [
      { resource: 'stamina', amount: 20, mode: 'flat' },
      { resource: 'hp', amount: 30, mode: 'flat' },
    ],
    effects: [
      { type: 'buff', modifiers: { damagePercent: 0.25, physDef: 15, attackSpeedPercent: 0.10 }, duration_ms: 12000, durationType: 'fixed', stackBehavior: 'refresh', buffIdPrefix: 'furia_ancestral', activeFxId: 'fx_ancestral_fury_aura' },
    ],
    maxActiveInstances: 1,
    spellWords: 'Sanguis Antiquus',
    chatWords: 'POR LOS ANCESTROS!',
    requirements: {
      minLevel: 10,
      allowedClasses: ['warrior', 'paladin', 'bandit', 'pirate'],
      allowedRaces: ['orc'],
    },
    tags: ['offensive', 'defensive', 'hybrid', 'pvp'],
  },

  // =========================================================================
  // 25. RESURRECCION -- Revive dead player (high cost)
  // =========================================================================
  spell_resurreccion: {
    id: 'spell_resurreccion',
    affects: [SELF, ALLY_PLAYER],
    name: 'Resurrection',
    nameEs: 'Resurrección',
    description: 'Revives a dead player at 30% HP. Costs 50% of your current HP and mana.',
    descriptionEs: 'Revive a un jugador muerto con 30% de HP. Cuesta 50% de tu HP y maná actual.',
    icon: 'spell_resurreccion',
    castType: 'target',
    targetType: 'players',
    range: 3,
    castTime: 3.0,
    interruptible: true,
    cooldown: 180,
    costs: [
      { resource: 'hp', amount: 0.50, mode: 'percent' },
      { resource: 'mana', amount: 0.50, mode: 'percent' },
    ],
    effects: [
      { type: 'resurrect', healPercent: 0.30 },
    ],
    maxActiveInstances: 1,
    spellWords: 'Vita Eternum... Surge',
    requirements: { minLevel: 25, allowedClasses: ['cleric', 'paladin'] },
    tags: ['support', 'heal', 'high_risk', 'hybrid'],
  },

  // =========================================================================
  // 26. ENRAIZAR -- Inmoviliza al objetivo con raíces (sí puede girar/atacar)
  // =========================================================================
  spell_enraizar: {
    id: 'spell_enraizar',
    affects: [HOSTILE_NPC, ENEMY_PLAYER],
    name: 'Entangle',
    nameEs: 'Enraizar',
    description: 'Roots a target in place, immobilizing them (they can still turn and act).',
    descriptionEs: 'Ata al objetivo con raíces, inmovilizándolo (puede girar y actuar, no caminar).',
    icon: 'spell_veneno_mortal',
    castType: 'target',
    targetType: 'players_and_npcs',
    range: 7,
    castTime: 0.5,
    interruptible: true,
    cooldown: 12,
    costs: [{ resource: 'mana', amount: 20, mode: 'flat' }],
    effects: [
      { type: 'status_effect', statusType: 'immobilize', duration_ms: IMPAIR.COMMON_IMMOBILIZE_MS, stackBehavior: 'refresh' },
    ],
    onHitFxId: 'fx_root_snare',
    maxActiveInstances: 1,
    spellWords: 'Radix Vincire',
    requirements: { minLevel: 6, allowedClasses: ['druid', 'wizard'] },
    tags: ['debuff', 'crowd_control', 'movement', 'pvp'],
  },

  // =========================================================================
  // 27. PARALIZAR -- Paraliza al objetivo (no camina NI gira)
  // =========================================================================
  spell_paralizar: {
    id: 'spell_paralizar',
    affects: [HOSTILE_NPC, ENEMY_PLAYER],
    name: 'Paralyze',
    nameEs: 'Paralizar',
    description: "Paralyzes a target: they cannot move nor turn (but can still act).",
    descriptionEs: 'Paraliza al objetivo: no puede moverse ni girar el heading (aún puede actuar).',
    icon: 'spell_congelar',
    castType: 'target',
    targetType: 'players_and_npcs',
    range: 6,
    castTime: 0.7,
    interruptible: true,
    cooldown: 16,
    costs: [{ resource: 'mana', amount: 30, mode: 'flat' }],
    effects: [
      { type: 'status_effect', statusType: 'paralyze', duration_ms: IMPAIR.COMMON_PARALYZE_MS, stackBehavior: 'refresh' },
    ],
    onHitFxId: 'fx_paralyze_cast',
    maxActiveInstances: 1,
    spellWords: 'Saxum Statua',
    requirements: { minLevel: 10, allowedClasses: ['wizard', 'cleric'] },
    tags: ['debuff', 'crowd_control', 'movement', 'pvp'],
  },

  // =========================================================================
  // 28. LIBERAR -- Devuelve la movilidad a un aliado (target)
  // =========================================================================
  spell_liberar: {
    id: 'spell_liberar',
    affects: [SELF, ALLY_PLAYER],
    name: 'Free Movement',
    nameEs: 'Liberar',
    description: 'Frees an ally from immobilize and paralyze, restoring their mobility.',
    descriptionEs: 'Libera a un aliado de inmovilizar y paralizar, devolviéndole la movilidad.',
    icon: 'spell_regeneracion',
    castType: 'target',
    targetType: 'players',
    range: 7,
    castTime: 0.3,
    interruptible: false,
    cooldown: 8,
    costs: [{ resource: 'mana', amount: 18, mode: 'flat' }],
    effects: [{ type: 'remove_impair' }],
    maxActiveInstances: 1,
    spellWords: 'Liber Motus',
    requirements: { minLevel: 5, allowedClasses: ['cleric', 'paladin', 'bard'] },
    tags: ['support', 'movement', 'hybrid'],
  },

  // =========================================================================
  // 29. LIBERACIÓN COLECTIVA -- Devuelve movilidad en área (support)
  // =========================================================================
  spell_liberar_area: {
    id: 'spell_liberar_area',
    affects: [SELF, ALLY_PLAYER],
    name: 'Mass Free Movement',
    nameEs: 'Liberación Colectiva',
    description: 'Frees everyone in a 3x3 area from immobilize and paralyze.',
    descriptionEs: 'Libera de inmovilizar y paralizar a todos en un área de 3x3.',
    icon: 'spell_curar_heridas',
    castType: 'area',
    targetType: 'ground',
    range: 6,
    areaSize: 3,
    castTime: 0.8,
    interruptible: true,
    cooldown: 20,
    costs: [{ resource: 'mana', amount: 35, mode: 'flat' }],
    effects: [{ type: 'remove_impair' }],
    maxActiveInstances: 1,
    spellWords: 'Liber Motus Magna',
    requirements: { minLevel: 12, allowedClasses: ['cleric', 'paladin', 'bard'] },
    tags: ['support', 'movement', 'hybrid'],
  },

  // =========================================================================
  // 30. LIBERACIÓN PROPIA -- Devuelve la movilidad a uno mismo (self)
  // =========================================================================
  spell_liberacion_propia: {
    id: 'spell_liberacion_propia',
    affects: [SELF],
    name: 'Self Free Movement',
    nameEs: 'Liberación Propia',
    description: 'Frees yourself from immobilize and paralyze.',
    descriptionEs: 'Te libera a vos mismo de inmovilizar y paralizar.',
    icon: 'spell_regeneracion',
    castType: 'self',
    targetType: 'self',
    range: 0,
    castTime: 0,
    interruptible: false,
    cooldown: 14,
    costs: [{ resource: 'mana', amount: 22, mode: 'flat' }],
    effects: [{ type: 'remove_impair' }],
    maxActiveInstances: 1,
    spellWords: 'Liber Me',
    requirements: { minLevel: 8 },
    tags: ['support', 'movement'],
  },

  // =========================================================================
  // 31. IMPETU -- Buff temporal a uno mismo, corto (5s). Demuestra affects: [SELF].
  // =========================================================================
  spell_impetu: {
    id: 'spell_impetu',
    affects: [SELF],
    name: 'Impetus',
    nameEs: 'Ímpetu',
    description: 'A short surge of speed and power on yourself (5s).',
    descriptionEs: 'Un breve estallido de velocidad y poder sobre vos mismo (5s).',
    // TODO(arte): reusa el icono de Furia Berserker como placeholder; falta icono propio.
    icon: 'spell_furia_berserker',
    castType: 'self',
    targetType: 'self',
    range: 0,
    castTime: 0,
    interruptible: false,
    cooldown: 12,
    costs: [{ resource: 'stamina', amount: 15, mode: 'flat' }],
    // Referencia del ciclo de FX de buffs: usa los 4 momentos (cast/apply/active/expire).
    castFxId: 'fx_electric_zap_impact',
    effects: [
      { type: 'buff', modifiers: { attackSpeedPercent: 0.20, moveSpeedPercent: 0.15 }, duration_ms: 5000, durationType: 'fixed', stackBehavior: 'override', buffIdPrefix: 'impetu', applyFxId: 'fx_electric_zap_impact', activeFxId: 'fx_arcane_swirl', expireFxId: 'fx_ice_spark_impact' },
    ],
    maxActiveInstances: 1,
    spellWords: 'Celeritas',
    requirements: { minLevel: 3, allowedClasses: ['warrior', 'pirate', 'bandit', 'hunter', 'rogue'] },
    tags: ['support', 'movement', 'pvp'],
  },

  // =========================================================================
  // PROVOCACIÓN (taunt) -- fuerza a los enemigos cercanos a venir a pegarte melee.
  // Self-cast instantáneo; el efecto `taunt` lo aplica GameRoom por radio. Ver docs/TAUNT.md.
  // =========================================================================
  spell_taunt: {
    id: 'spell_taunt',
    affects: [SELF],
    name: 'Taunt',
    nameEs: 'Provocación',
    description: 'Force nearby enemies to drop everything and come attack you in melee.',
    descriptionEs: 'Fuerza a los enemigos cercanos a soltar todo y venir a pegarte cuerpo a cuerpo.',
    // TODO(arte): reusa el icono del Grito de Guerra como placeholder.
    icon: 'spell_grito_guerra',
    castType: 'self',
    targetType: 'self',
    range: 0,
    castTime: 0,
    interruptible: false,
    cooldown: 10,
    costs: [{ resource: 'stamina', amount: 20, mode: 'flat' }],
    effects: [{ type: 'taunt', radius: 1, durationMs: 3000 }],
    maxActiveInstances: 1,
    spellWords: '¡A mí!',
    requirements: { minLevel: 1, allowedClasses: ['warrior', 'paladin'] },
    tags: ['pvp', 'pve'],
  },

  // =========================================================================
  // CONTROL DE INVOCACIONES — tomar el control de la invocación enemiga apuntada.
  // Brujo: temporal (8s). Monje: permanente (técnica marcial/espiritual, cuesta stamina).
  // Ver docs/SUMMONS.md.
  // =========================================================================
  spell_dominate_summon: {
    id: 'spell_dominate_summon',
    affects: [HOSTILE_NPC, ENEMY_PLAYER],
    name: 'Dominate Summon',
    nameEs: 'Dominar Invocación',
    description: "Seize control of an enemy's summon for 8 seconds.",
    descriptionEs: 'Arrebatás el control de la invocación de un enemigo por 8 segundos.',
    icon: 'spell_pacto_sangre',
    castType: 'target',
    targetType: 'players_and_npcs',
    range: 8,
    castTime: 0.8,
    interruptible: true,
    cooldown: 20,
    costs: [{ resource: 'mana', amount: 40, mode: 'flat' }],
    effects: [{ type: 'control_summon', durationMs: 8000 }],
    maxActiveInstances: 1,
    spellWords: 'Servus Meus',
    requirements: { minLevel: 1, allowedClasses: ['warlock'] },
    tags: ['pvp'],
  },
  spell_soul_capture: {
    id: 'spell_soul_capture',
    affects: [HOSTILE_NPC, ENEMY_PLAYER],
    name: 'Soul Capture',
    nameEs: 'Captura de Alma',
    description: "Bind an enemy's summon to your will — permanently.",
    descriptionEs: 'Atás el alma de la invocación enemiga a tu voluntad, para siempre.',
    icon: 'spell_grito_guerra',
    castType: 'target',
    targetType: 'players_and_npcs',
    range: 6,
    castTime: 1.2,
    interruptible: true,
    cooldown: 60,
    costs: [{ resource: 'stamina', amount: 40, mode: 'flat' }],
    effects: [{ type: 'control_summon', durationMs: 0 }],
    maxActiveInstances: 1,
    spellWords: 'Anima Vincta',
    requirements: { minLevel: 1, allowedClasses: ['monk'] },
    tags: ['pvp'],
  },

  // =========================================================================
  // DESVINCULAR -- Disipa una invocación PROPIA apuntada (libera un slot del límite
  // de invocaciones). Dos versiones, sin gate de clase (se compran en el vendor):
  // maná (mágica) y marcial (estamina, sin magia). Ver docs/SUMMONS.md.
  // =========================================================================
  spell_desvincular: {
    id: 'spell_desvincular',
    affects: [SpellAffiliation.OWN_SUMMON],
    name: 'Unsummon',
    nameEs: 'Desvincular',
    description: "Dismiss one of your own summons, freeing a summon slot.",
    descriptionEs: 'Disipa una de tus invocaciones (la que apuntes), liberando un espacio de invocación.',
    // TODO(arte): reusa el icono de Pacto de Sangre como placeholder; falta icono propio.
    icon: 'spell_pacto_sangre',
    castType: 'target',
    targetType: 'players_and_npcs',
    range: 10,
    castTime: 0,
    interruptible: false,
    cooldown: 2,
    costs: [{ resource: 'mana', amount: 15, mode: 'flat' }],
    effects: [{ type: 'unsummon' }],
    maxActiveInstances: 1,
    spellWords: 'Vale, criatura',
    requirements: { minLevel: 1 },
    tags: ['summon', 'support'],
  },
  spell_desvincular_marcial: {
    id: 'spell_desvincular_marcial',
    affects: [SpellAffiliation.OWN_SUMMON],
    name: 'Release Bond',
    nameEs: 'Soltar Vínculo',
    description: "Release one of your own summons through a martial technique — no magic.",
    descriptionEs: 'Soltás una de tus invocaciones con una técnica marcial (sin magia), liberando un espacio.',
    // TODO(arte): reusa el icono de Grito de Guerra como placeholder; falta icono propio.
    icon: 'spell_grito_guerra',
    castType: 'target',
    targetType: 'players_and_npcs',
    range: 10,
    castTime: 0,
    interruptible: false,
    cooldown: 2,
    costs: [{ resource: 'stamina', amount: 15, mode: 'flat' }],
    effects: [{ type: 'unsummon' }],
    maxActiveInstances: 1,
    // Técnica marcial: sin palabras mágicas.
    requirements: { minLevel: 1 },
    tags: ['summon', 'support'],
  },

  // =========================================================================
  // 32. EGIDA ALIADA -- Buff defensivo a un aliado apuntado (20s). affects: [SELF, ALLY_PLAYER].
  // =========================================================================
  spell_egida_aliada: {
    id: 'spell_egida_aliada',
    affects: [SELF, ALLY_PLAYER],
    name: 'Allied Aegis',
    nameEs: 'Égida Aliada',
    description: 'Shields an ally, boosting their defenses for 20 seconds.',
    descriptionEs: 'Protege a un aliado, aumentando sus defensas por 20 segundos.',
    // TODO(arte): reusa el icono de Escudo Divino como placeholder; falta icono propio.
    icon: 'spell_escudo_divino',
    castType: 'target',
    targetType: 'players',
    range: 6,
    castTime: 0.5,
    interruptible: true,
    cooldown: 12,
    costs: [{ resource: 'mana', amount: 25, mode: 'flat' }],
    effects: [
      { type: 'buff', modifiers: { physDef: 15, magicDef: 15, regenHP: 1.5 }, duration_ms: 20000, durationType: 'fixed', stackBehavior: 'refresh', buffIdPrefix: 'egida_aliada', applyFxId: 'fx_priest_heal_burst', activeFxId: 'fx_shield_dome_aura' },
    ],
    maxActiveInstances: 1,
    spellWords: 'Tutela Socii',
    requirements: { minLevel: 6, allowedClasses: ['cleric', 'paladin', 'bard', 'druid'] },
    tags: ['support', 'defensive', 'hybrid'],
  },

  // =========================================================================
  // 33. ESTANDARTE DE FACCION -- Buff largo (50s) a aliados de facción en pantalla.
  //     affects: [SELF, ALLY_PLAYER] → NO buffea rivales (a diferencia del viejo screen).
  // =========================================================================
  spell_estandarte_faccion: {
    id: 'spell_estandarte_faccion',
    affects: [SELF, ALLY_PLAYER],
    name: 'Faction Banner',
    nameEs: 'Estandarte de Facción',
    description: 'Raises a banner that empowers all faction allies on screen for 50 seconds.',
    descriptionEs: 'Alza un estandarte que fortalece a todos los aliados de facción en pantalla por 50 segundos.',
    // TODO(arte): reusa el icono de Canción del Valor como placeholder; falta icono propio.
    icon: 'spell_cancion_valor',
    castType: 'screen',
    targetType: 'players',
    range: 0,
    castTime: 1.5,
    interruptible: true,
    cooldown: 60,
    costs: [{ resource: 'mana', amount: 40, mode: 'flat' }],
    effects: [
      { type: 'buff', modifiers: { damagePercent: 0.08, physDef: 8, magicDef: 8 }, duration_ms: 50000, durationType: 'fixed', stackBehavior: 'refresh', buffIdPrefix: 'estandarte_faccion', activeFxId: 'fx_holy_circle_aura' },
    ],
    maxActiveInstances: 1,
    spellWords: 'Vexillum Gloriae',
    requirements: { minLevel: 14, allowedClasses: ['paladin', 'bard', 'cleric'] },
    tags: ['support', 'hybrid', 'pvp'],
  },

  // =========================================================================
  // 35. PASO SOMBRÍO -- Mago: blink puro a la espalda del objetivo
  // =========================================================================
  spell_shadowstep: {
    id: 'spell_shadowstep',
    // Reposicionamiento puro: el effect blink_behind mueve al caster (fuera del loop
    // de targets). Requiere target activo (enemigo) como ancla.
    affects: [HOSTILE_NPC, ENEMY_PLAYER],
    name: 'Shadow Step',
    nameEs: 'Paso Sombrío',
    description: "Blink behind your target, appearing at its back within range.",
    descriptionEs: 'Parpadea detrás de tu objetivo, apareciendo a su espalda dentro del rango.',
    // TODO(arte): reusa el icono de Teletransporte como placeholder; falta icono propio.
    icon: 'spell_teletransporte',
    castType: 'target',
    targetType: 'players_and_npcs',
    range: 8,
    castTime: 0,
    interruptible: false,
    cooldown: 12,
    costs: [{ resource: 'mana', amount: 25, mode: 'flat' }],
    effects: [{ type: 'blink_behind' }],
    maxActiveInstances: 1,
    spellWords: 'Umbra Gradus',
    requirements: { minLevel: 10, allowedClasses: ['wizard'] },
    tags: ['movement', 'pvp', 'hybrid'],
  },

  // =========================================================================
  // 36. GOLPE ARTERO -- Asesino: blink a la espalda + golpe (comparte CD melee)
  // =========================================================================
  spell_backstab_strike: {
    id: 'spell_backstab_strike',
    affects: [HOSTILE_NPC, ENEMY_PLAYER],
    name: 'Backstab Strike',
    nameEs: 'Golpe Artero',
    description: "Blink behind your target and strike its back. Shares the melee attack cooldown.",
    descriptionEs:
      'Parpadea detrás de tu objetivo y golpeás su espalda. Comparte el cooldown del golpe cuerpo a cuerpo.',
    // TODO(arte): reusa el icono de Veneno Mortal como placeholder; falta icono propio.
    icon: 'spell_veneno_mortal',
    castType: 'target',
    targetType: 'players_and_npcs',
    range: 6,
    castTime: 0,
    interruptible: false,
    cooldown: 8,
    sharesMeleeCooldown: true,
    requiredWeaponSubtypes: ['dagger'],
    costs: [{ resource: 'stamina', amount: 20, mode: 'flat' }],
    // Blink primero (reposiciona a la espalda), luego el golpe físico. Con daga + el
    // flag de backstab (keystone drow / ítem), el golpe cae por la espalda ⇒ crítico.
    effects: [
      { type: 'blink_behind' },
      { type: 'damage', damageType: 'physical', minDamage: 12, maxDamage: 22 },
    ],
    maxActiveInstances: 1,
    spellWords: 'Ictus Dorsi',
    requirements: { minLevel: 10, allowedClasses: ['rogue'] },
    tags: ['offensive', 'movement', 'pvp', 'hybrid'],
  },

  // =========================================================================
  // 37. GUARDIA TOTAL -- Paladín: buff corto de bloqueo omnidireccional
  // =========================================================================
  spell_guardia_total: {
    id: 'spell_guardia_total',
    affects: [SELF],
    name: 'Total Guard',
    nameEs: 'Guardia Total',
    description: 'For a few seconds you can block attacks from any direction, not just the front.',
    descriptionEs:
      'Por unos segundos podés bloquear golpes desde cualquier dirección, no sólo de frente, y aumentás tu probabilidad de bloqueo.',
    // TODO(arte): reusa el icono del Escudo Divino como placeholder; falta icono propio.
    icon: 'spell_escudo_divino',
    castType: 'self',
    targetType: 'self',
    range: 0,
    castTime: 0,
    interruptible: false,
    cooldown: 20,
    costs: [{ resource: 'stamina', amount: 15, mode: 'flat' }],
    effects: [
      {
        type: 'buff',
        modifiers: { blockOmnidirectional: 1, blockChance: 0.15 },
        duration_ms: 5000,
        durationType: 'fixed',
        stackBehavior: 'refresh',
        buffIdPrefix: 'guardia_total',
        activeFxId: 'fx_holy_circle_aura',
      },
    ],
    maxActiveInstances: 1,
    spellWords: 'Custodia Plena',
    requirements: { minLevel: 10, allowedClasses: ['paladin'] },
    tags: ['defensive', 'pvp'],
  },

  // =========================================================================
  // 38. EMBESTIDA -- Guerrero: carga al frente del objetivo + golpe (arma melee)
  // =========================================================================
  spell_embestida: {
    id: 'spell_embestida',
    affects: [HOSTILE_NPC, ENEMY_PLAYER],
    name: 'Charge',
    nameEs: 'Embestida',
    description: 'Charge in a straight line to your target and strike it. Requires a melee weapon.',
    descriptionEs:
      'Cargás en línea recta hasta tu objetivo y lo golpeás. Requiere un arma cuerpo a cuerpo.',
    // TODO(arte): reusa el icono de Ímpetu como placeholder; falta icono propio.
    icon: 'spell_impetu',
    castType: 'target',
    targetType: 'players_and_npcs',
    range: 6,
    castTime: 0,
    interruptible: false,
    cooldown: 10,
    requiredWeaponSubtypes: ['sword', 'axe', 'mace', 'dagger', 'knuckles'],
    costs: [{ resource: 'stamina', amount: 20, mode: 'flat' }],
    // Carga (self hacia el objetivo, se detiene adyacente) y luego el golpe físico.
    effects: [
      { type: 'displace', subject: 'self', mode: 'front_of_target', tiles: 6 },
      { type: 'damage', damageType: 'physical', minDamage: 10, maxDamage: 18 },
    ],
    maxActiveInstances: 1,
    spellWords: 'Impetus Bellum',
    chatWords: 'A LA CARGA!',
    requirements: { minLevel: 8, allowedClasses: ['warrior', 'paladin'] },
    tags: ['offensive', 'movement', 'pvp', 'hybrid'],
  },

  // =========================================================================
  // 39. ARPÓN -- Cazador: jala al objetivo hacia el caster
  // =========================================================================
  spell_atraccion: {
    id: 'spell_atraccion',
    affects: [HOSTILE_NPC, ENEMY_PLAYER],
    name: 'Harpoon',
    nameEs: 'Arpón',
    description: 'Yank your target several tiles toward you.',
    descriptionEs: 'Arrastrás a tu objetivo varios tiles hacia vos.',
    // TODO(arte): reusa el icono de Enraizar como placeholder; falta icono propio.
    icon: 'spell_enraizar',
    castType: 'target',
    targetType: 'players_and_npcs',
    range: 7,
    minRange: 2,
    castTime: 0,
    interruptible: false,
    cooldown: 12,
    costs: [{ resource: 'stamina', amount: 20, mode: 'flat' }],
    effects: [{ type: 'displace', subject: 'target', mode: 'toward_caster', tiles: 4 }],
    maxActiveInstances: 1,
    spellWords: 'Tractus',
    requirements: { minLevel: 10, allowedClasses: ['hunter', 'pirate'] },
    tags: ['movement', 'pvp'],
  },
};

// ============================================================================
// HELPERS
// ============================================================================

export function getSpellDefinition(spellId: string): SpellDefinition | undefined {
  return SPELLS[spellId];
}

export function getAllSpellDefinitions(): SpellDefinition[] {
  return Object.values(SPELLS);
}

export function getSpellsByTag(tag: string): SpellDefinition[] {
  return Object.values(SPELLS).filter(s => s.tags.includes(tag as SpellDefinition['tags'][number]));
}

/**
 * Reglas de gating por clase:
 *  1. Si requirements.allowedClasses está seteada → sólo esas clases (forbiddenClasses se ignora).
 *  2. Si no, y requirements.forbiddenClasses está seteada → cualquier clase menos esas.
 *  3. Si no hay ninguno → spell global (cualquier clase puede aprenderlo).
 */
export function canClassLearnSpell(classId: CharacterClassId, spellId: string): boolean {
  const spell = SPELLS[spellId];
  if (!spell) return false;
  const { allowedClasses, forbiddenClasses } = spell.requirements;
  if (allowedClasses && allowedClasses.length > 0) {
    return allowedClasses.includes(classId);
  }
  if (forbiddenClasses && forbiddenClasses.length > 0) {
    return !forbiddenClasses.includes(classId);
  }
  return true;
}

export function getSpellsForClass(classId: CharacterClassId): SpellDefinition[] {
  return Object.values(SPELLS).filter((s) => canClassLearnSpell(classId, s.id));
}

/**
 * Reglas de gating por raza (espeja canClassLearnSpell):
 *  1. Si requirements.allowedRaces está seteada → sólo esas razas.
 *  2. Si no, y requirements.forbiddenRaces está seteada → cualquier raza menos esas.
 *  3. Si ninguno → sin gating de raza.
 */
export function canRaceLearnSpell(raceId: CharacterRaceId, spellId: string): boolean {
  const spell = SPELLS[spellId];
  if (!spell) return false;
  const { allowedRaces, forbiddenRaces } = spell.requirements;
  if (allowedRaces && allowedRaces.length > 0) {
    return allowedRaces.includes(raceId);
  }
  if (forbiddenRaces && forbiddenRaces.length > 0) {
    return !forbiddenRaces.includes(raceId);
  }
  return true;
}

/**
 * True si el spell no tiene restricciones de clase NI de raza (es global).
 */
export function isGlobalSpell(spell: SpellDefinition): boolean {
  const { allowedClasses, forbiddenClasses, allowedRaces, forbiddenRaces } = spell.requirements;
  const hasAllowedClass = !!allowedClasses && allowedClasses.length > 0;
  const hasForbiddenClass = !!forbiddenClasses && forbiddenClasses.length > 0;
  const hasAllowedRace = !!allowedRaces && allowedRaces.length > 0;
  const hasForbiddenRace = !!forbiddenRaces && forbiddenRaces.length > 0;
  return !hasAllowedClass && !hasForbiddenClass && !hasAllowedRace && !hasForbiddenRace;
}

// ============================================================================
// FX DE BUFFS — registros derivados del catálogo (ver docs/PARTICLES.md §FX de buffs)
// ============================================================================

/**
 * Set con TODOS los `activeFxId` declarados por effects buff/debuff del catálogo.
 * El server lo usa en `refreshBuffFx` para saber qué ids de `auraFxIds` son
 * "propiedad de un buff" y puede reconciliar (agregar/quitar) sin pisar otros FX
 * persistentes (silencio, glow de héroe, auras toggle). Se computa una vez.
 */
export const BUFF_ACTIVE_FX_IDS: ReadonlySet<string> = (() => {
  const ids = new Set<string>();
  for (const spell of Object.values(SPELLS)) {
    for (const effect of spell.effects) {
      if ((effect.type === 'buff' || effect.type === 'debuff') && effect.activeFxId) {
        ids.add(effect.activeFxId);
      }
    }
  }
  return ids;
})();

/**
 * Mapa `activeFxId → expireFxId` para todos los buffs que definan ambos. El cliente
 * lo usa para disparar el one-shot de expiración cuando un `activeFxId` sale de
 * `auraFxIds` (sin mensaje del server). Si varios buffs comparten `activeFxId`, gana
 * el primero (edge case improbable).
 */
export const BUFF_EXPIRE_FX_BY_ACTIVE: ReadonlyMap<string, string> = (() => {
  const map = new Map<string, string>();
  for (const spell of Object.values(SPELLS)) {
    for (const effect of spell.effects) {
      if (
        (effect.type === 'buff' || effect.type === 'debuff') &&
        effect.activeFxId &&
        effect.expireFxId &&
        !map.has(effect.activeFxId)
      ) {
        map.set(effect.activeFxId, effect.expireFxId);
      }
    }
  }
  return map;
})();
